<template>
    <div class="tw-relative tw-flex tw-rounded-lg">
        <span
            v-if="slots.prepend"
            class="tw-content-center tw-bg-white tw-border tw-border-r-0 tw-border-gray-300 tw-text-gray-500 tw-px-3.5 tw-py-1.5 tw-rounded-l-md tw-select-none tw-text-sm"
            :class="prependClass"
        >
            <slot name="prepend">Prepend</slot>
        </span>
        <span
            v-if="currency"
            class="tw-content-center tw-bg-white tw-border tw-border-r-0 tw-border-gray-300 tw-text-gray-500 tw-px-3.5 tw-py-1.5 tw-rounded-l-md tw-select-none tw-text-sm"
            :class="[
                currencyClass,
                {
                    'tw-rounded-l-none': slots.prepend,
                    '!tw-bg-white/10 !tw-text-white !tw-border-white/30': glass,
                    '!tw-border-red-400 !tw-bg-red-50 !tw-text-red-500': error,
                    '!tw-border-amber-400 !tw-bg-amber-50 !tw-text-amber-600': warning && !error,
                },
            ]"
        >
            {{ currency }}
        </span>
        <input
            :id="name"
            v-model="model"
            :readonly="readonly"
            :placeholder="placeholder"
            :disabled="disabled || loading"
            :required="required"
            :name="name"
            type="number"
            :step="step"
            :min="min"
            :max="max"
            class="tw-text-sm tw-border tw-border-gray-300 tw-bg-white tw-w-full tw-pl-3.5 tw-py-1.5 tw-rounded-md hover:tw-border-gray-400 focus:tw-border-gray-600 focus:tw-ring-0 focus:tw-outline-none tw-transition-colors tw-duration-150"
            :class="[
                inputClass,
                {
                    'tw-rounded-l-none tw-pr-1': currency,
                    '!tw-bg-white/10 !tw-text-white !tw-border-white/30 placeholder:!tw-text-white/50 hover:!tw-bg-white/15 hover:!tw-border-white/50 focus:!tw-bg-white/15 focus:!tw-border-white/70':
                        glass,
                    '!tw-bg-gray-50 tw-opacity-75': readonly || disabled,
                    '!tw-border-red-400 !tw-bg-red-50': error,
                    '!tw-border-amber-400 !tw-bg-amber-50': warning && !error,
                },
            ]"
            @input="validateInput"
        />
        <ContainedLoader v-if="loading" />
    </div>
</template>

<script setup lang="ts">
import ContainedLoader from '@/js/components/controls/ContainedLoader.vue';
import { useSlots, watch } from 'vue';

const { dp, max, min } = defineProps({
    name: {
        type: String,
        default: null,
    },
    dp: {
        type: [Number],
        required: false,
        default: null,
    },
    min: {
        type: [Number, String],
        default: null,
    },
    step: {
        type: [Number, String],
        default: 1,
    },
    max: {
        type: [Number],
        default: null,
    },
    currency: {
        type: String,
        default: null,
    },
    required: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    placeholder: {
        type: String,
        default: null,
    },
    inputClass: {
        type: [String, Array, Object],
        default: null,
    },
    currencyClass: {
        type: [String, Array, Object],
        default: null,
    },
    prependClass: {
        type: [String, Array, Object],
        default: null,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: Boolean,
        default: false,
    },
    warning: {
        type: Boolean,
        default: false,
    },
    glass: {
        type: Boolean,
        default: false,
    },
});

const slots = useSlots();

const model = defineModel<string | number | null>({
    type: [String, Number],
    default: 0,
});

// If dp passed in then dont allow decimals more than dp
watch(model, () => {
    if (!model.value) {
        return;
    }
    const decimals = model.value?.toString().split('.')[1];
    if (dp && decimals && decimals.length > dp) {
        model.value = parseFloat(model.value.toString()).toFixed(dp);
    }
});

// runtime guard: number primitive only
const isNumericMin = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const validateInput = () => {
    if (!model.value) {
        return;
    }
    if (max && parseInt(model.value.toString()) > max) {
        model.value = max;
    }
    // if min is numeric, apply the min limit
    if (isNumericMin(min) && parseInt(model.value.toString()) < min) {
        model.value = min;
    }
};
</script>
